import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from "@angular/core";
import {Subscription} from "rxjs";
import {ResponsiveService} from "../../orchestration/responsive.service";
import {IdentityEvents} from "../../identity/identity-events";
import {IdentityModel} from "../../identity/identity.model";
import {SessionEvents} from "../../orchestration/session-events";
import {StringUtils} from "../../string-utils/string-utils";
import {LoyaltyCardData, LoyaltyCardType} from "../../identity/member-summary-entity.model";

@Component({
    selector: "woa-loyalty-card",
    templateUrl: "./loyalty-card.component.html"
})
export class LoyaltyCardComponent implements OnInit, OnDestroy {
    @Input() card: LoyaltyCardData;
    @Input() index: number;
    @Input() buttonMode: boolean = false;
    @Output() onRedeem: EventEmitter<void> = new EventEmitter();

    LoyaltyCardType = LoyaltyCardType;
    StringUtils = StringUtils;

    stampRows: number = 1;
    stampColumns: number = 1;
    stampPlaceholderContainerSize: number = 0;
    stampIcon: string;
    private _content: ElementRef;
    private _countdown: ElementRef;
    private subscriptions: Subscription = new Subscription();

    @ViewChild("content")
    private set content(content: ElementRef) {
        if (content) {
            this._content = content;
            this.refresh();
        }
    }

    @ViewChild("stamps")
    private set stamps(countdown: ElementRef) {
        if (countdown) {
            this._countdown = countdown;
            this.refresh();
        }
    }

    constructor(private changeDetectorRef: ChangeDetectorRef,
                private responsiveService: ResponsiveService,
                public identityModel: IdentityModel) {
    }

    ngOnInit(): void {
        this.subscriptions.add(SessionEvents.currentApp$.subscribe(() => {
            setTimeout(() => {
                this.refresh();
                this.changeDetectorRef.detectChanges();
                setTimeout(() => {
                    this.refresh();
                    this.changeDetectorRef.detectChanges();
                });
            });
        }));
        this.subscriptions.add(IdentityEvents.currentMember$.subscribe(() => {
            this.refresh();
            this.changeDetectorRef.detectChanges();
        }));
        this.subscriptions.add(this.responsiveService.windowWidth$.subscribe(() => {
            this.refresh();
            this.changeDetectorRef.detectChanges();
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private refresh(): void {
        if (!this.card || !this._content || !this._content.nativeElement) {
            return;
        }

        if (this.card.cardType === LoyaltyCardType.STAMPS) {
            const spec: string = getComputedStyle(this._content.nativeElement).getPropertyValue("--themed-loyalty-card-max-column-spec").trim();
            const maxCols: number[] = spec.split(/[, ]/).map((s) => parseInt(s.trim()));
            this.stampRows = (() => {
                for (let rows: number = 1; rows <= maxCols.length; rows++) {
                    if (this.card.goal <= (rows * maxCols[rows - 1])) {
                        return rows;
                    }
                }
                return 0;
            })();
            this.stampColumns = Math.ceil(this.card.goal / this.stampRows);
            this.stampIcon = getComputedStyle(this._content.nativeElement).getPropertyValue("--themed-loyalty-card-stamp-icon").trim();

            if (this._countdown && this._content.nativeElement) {
                const cellHeight = Math.floor(this._countdown.nativeElement.offsetHeight / this.stampRows);
                const cellWidth = Math.floor(this._countdown.nativeElement.offsetWidth / this.stampColumns);
                this.stampPlaceholderContainerSize = Math.min(cellHeight, cellWidth, 65);
            }
        }
    }

    span(className: string, id: string, content: any): string {
        return "<span class='" + className + "' id='" + id + "'>" + content + "</span>";
    }

    get cells(): Array<number> {
        return Array.from(Array(this.card.goal), (_, x) => x);
    }

    getStampRotation(stampNr: number): string {
        return (((this.card.name.charCodeAt(stampNr % this.card.name.length) * 13) % 360) - 180) + "deg";
    }

    getStampGrungeOffset(stampNr: number): string {
        return ((this.card.name.charCodeAt(stampNr % this.card.name.length) * 25) % 200) + "px";
    }

    get cardTheme(): string {
        return this.card.theme ? this.card.theme.replace(" ", "_") : "default";
    }

    get footerText(): string {
        return this.card.cardType === LoyaltyCardType.STAMPS ? "woa.message.loyalty-card.stampsRequiredText"
            : this.card.pointsRequiredToRedeem > this.identityModel.currentMember.pointsBalance ? "woa.message.loyalty-card.pointsRequiredText"
                : "woa.message.loyalty-card.redeemPointsText";
    }

    get stampsRequired(): number {
        return this.card.goal - this.card.purchaseCount;
    }

    get pointsRequired(): string {
        return StringUtils.toScale(Math.max(0, this.card.pointsRequiredToRedeem - this.identityModel.currentMember.pointsBalance), 4);
    }
}



// WEBPACK FOOTER //
// ./src/app/loyalty-card/loyalty-card/loyalty-card.component.ts